import React, { useState } from 'react';
import { useNode } from '@craftjs/core';
import { ChevronDown } from 'lucide-react';
import { isBlankHtml } from '@/utils/functions';
import { htmlSeguro } from '../utils/htmlSeguro';

export type EstiloAcordeon = 'minimal' | 'bordered' | 'cards';

interface ItemAcordeon {
  question?: string;
  answer?: string;
}

interface AccordionBlockProps {
  items?: ItemAcordeon[];
  style?: EstiloAcordeon;
  allowMultiple?: boolean;
  openFirst?: boolean;
  questionColor?: string;
  answerColor?: string;
  accentColor?: string;
  borderColor?: string;
  padding?: number;
}

/**
 * Clases del contenedor de cada ítem. Mismas tres variantes que la sección `FAQ`,
 * para que un acordeón suelto y uno dentro de FAQ se vean igual.
 *
 * MANTENER EN SYNC con goautos-admin.
 */
export const claseItemAcordeon = (estilo: EstiloAcordeon) => {
  const base = 'transition-all duration-200 overflow-hidden';
  switch (estilo) {
    case 'bordered':
      return `${base} border rounded-xl px-6`;
    case 'cards':
      return `${base} border rounded-xl px-6 shadow-sm hover:shadow-md`;
    case 'minimal':
    default:
      return `${base} border-b px-2`;
  }
};

/**
 * Acordeón — render del sitio público.
 *
 * El bloque suelto equivalente a la sección `FAQ`, que sigue existiendo con su
 * título y su fondo. Comparte el aspecto y el comportamiento de abrir/cerrar,
 * pero no su estado interno: son componentes distintos y tocarlos por separado
 * evita cambiar lo que ya está publicado.
 *
 * Espejo de goautos-admin/src/components/builder2/userComponents/AccordionBlock.tsx.
 */
export const AccordionBlock = ({
  items = [],
  style = 'minimal',
  allowMultiple = false,
  openFirst = true,
  questionColor = '#111827',
  answerColor = '#4b5563',
  accentColor = '#1e88e5',
  borderColor = '#e5e7eb',
  padding = 0,
}: AccordionBlockProps) => {
  const { connectors } = useNode();
  const [abiertos, setAbiertos] = useState<number[]>(openFirst ? [0] : []);

  const alternar = (i: number) => {
    setAbiertos((previos) => {
      if (previos.includes(i)) return previos.filter((x) => x !== i);
      return allowMultiple ? [...previos, i] : [i];
    });
  };

  const visibles = (items ?? []).filter((item) => !isBlankHtml(item?.question));

  return (
    <div
      ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }}
      style={{
        width: '100%',
        padding: `${padding}px`,
        // Igual que en `Section`: el editor lo necesita para anclar sus controles
        // y acá no cambia nada, pero mantiene idéntico el estilo computado.
        position: 'relative',
      }}
    >
      {visibles.map((item, i) => {
        const abierto = abiertos.includes(i);
        return (
          <div
            key={i}
            className={claseItemAcordeon(style)}
            style={{
              borderColor: style === 'minimal' ? `${borderColor}30` : borderColor,
              backgroundColor: style === 'cards' ? '#ffffff' : 'transparent',
              marginBottom: style === 'minimal' ? 0 : '12px',
            }}
          >
            <button
              type="button"
              className="w-full flex justify-between items-center text-left focus:outline-none py-5 gap-4"
              onClick={() => alternar(i)}
              aria-expanded={abierto}
            >
              <h3
                className="text-lg font-medium flex-1"
                style={{ color: questionColor }}
                dangerouslySetInnerHTML={htmlSeguro(item.question || '')}
              />
              <div
                className="flex-shrink-0 transition-transform duration-300"
                style={{ transform: abierto ? 'rotate(180deg)' : 'rotate(0deg)' }}
              >
                <ChevronDown size={20} style={{ color: accentColor }} />
              </div>
            </button>
            <div
              className="transition-all duration-300 ease-in-out"
              style={{ maxHeight: abierto ? '500px' : '0px', opacity: abierto ? 1 : 0, overflow: 'hidden' }}
            >
              <div
                className="pb-5 text-base leading-relaxed"
                style={{ color: answerColor }}
                dangerouslySetInnerHTML={htmlSeguro(item.answer || '')}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

AccordionBlock.craft = {
  displayName: 'AccordionBlock',
  props: {
    items: [
      { question: '¿Cómo funciona el financiamiento?', answer: 'Trabajamos con las principales instituciones y la aprobación suele salir en 24 horas.' },
      { question: '¿Los vehículos tienen garantía?', answer: 'Todos incluyen 6 meses de garantía mecánica.' },
      { question: '¿Reciben mi auto en parte de pago?', answer: 'Sí, lo tasamos sin costo y lo tomamos como pie.' },
    ],
    style: 'minimal',
    allowMultiple: false,
    openFirst: true,
    questionColor: '#111827',
    answerColor: '#4b5563',
    accentColor: '#1e88e5',
    borderColor: '#e5e7eb',
    padding: 0,
  },
};
